import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { JOB_SEO } from "@/data/jobSeoData";
import { ArrowRight, MapPin, Wallet } from "lucide-react";

/**
 * Hub page /metiers — lists all 12 unique job SEO pages, grouped by family.
 * Reinforces internal linking + drives Google to discover /metiers/:slug URLs.
 */
export default function MetiersHubPage() {
    const jobs = useMemo(() => Object.values(JOB_SEO), []);
    const families = useMemo(() => {
        const grouped = {};
        jobs.forEach((j) => {
            const key = j.family || "Autres";
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(j);
        });
        return grouped;
    }, [jobs]);

    useEffect(() => {
        document.title = "Métiers — Personnel qualifié Lyon, Grenoble, Valence | Renforteo";
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta(
            "description",
            "12 métiers vérifiés en Auvergne-Rhône-Alpes : serveur, chef de rang, cuisinier, plongeur, barman, hôtesse, livreur monteur, manutentionnaire, femme de ménage. Renforteo."
        );
        setMeta(
            "keywords",
            "métiers hôtellerie Lyon, personnel événementiel Grenoble, serveur cuisinier plongeur Valence, livreur monteur Ikea, hôtesse Sirha, manutention Eurexpo"
        );
        setMeta("og:title", "Métiers — Personnel qualifié AURA | Renforteo", "property");
        setMeta("og:type", "website", "property");
        setMeta("og:url", "https://renforteo.fr/metiers", "property");

        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", "https://renforteo.fr/metiers");

        const ldId = "renforteo-metiers-hub-jsonld";
        let ld = document.getElementById(ldId);
        if (!ld) {
            ld = document.createElement("script");
            ld.id = ldId;
            ld.type = "application/ld+json";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "CollectionPage",
                    name: "Métiers Renforteo",
                    description:
                        "12 pages métiers uniques couvrant restauration, hôtellerie, événementiel, logistique et propreté en Auvergne-Rhône-Alpes.",
                    url: "https://renforteo.fr/metiers",
                    inLanguage: "fr-FR",
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://renforteo.fr" },
                        { "@type": "ListItem", position: 2, name: "Métiers", item: "https://renforteo.fr/metiers" },
                    ],
                },
                {
                    "@type": "ItemList",
                    itemListElement: jobs.map((j, i) => ({
                        "@type": "ListItem",
                        position: i + 1,
                        name: j.h1,
                        url: `https://renforteo.fr/metiers/${j.slug}`,
                    })),
                },
            ],
        });
    }, [jobs]);

    return (
        <div data-testid="metiers-hub-page">
            <Header />

            {/* HERO */}
            <section
                className="relative overflow-hidden bg-[#0B192C] text-white"
                data-testid="metiers-hub-hero"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-xs text-white/70 mb-4">
                        <Link to="/" className="hover:text-[#F5A623]">Accueil</Link>
                        <span className="mx-2">/</span>
                        <span className="text-white">Métiers</span>
                    </div>
                    <div className="overline text-[#F5A623] mb-3">Nos 12 métiers vérifiés</div>
                    <h1
                        className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-5 max-w-3xl"
                        data-testid="metiers-hub-h1"
                    >
                        Personnel qualifié auto-entrepreneur en Auvergne-Rhône-Alpes
                    </h1>
                    <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed mb-8">
                        Renforteo met en relation les entreprises de Lyon, Grenoble et Valence avec
                        des prestataires indépendants vérifiés (SIRET, KBIS, RIB, pièce d'identité).
                        Choisissez le métier qui vous intéresse pour découvrir tarifs, missions
                        types et pré-requis.
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className="pill" style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623" }}>
                            🍽️ 7 métiers restauration
                        </span>
                        <span className="pill" style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623" }}>
                            💁 1 métier accueil / événementiel
                        </span>
                        <span className="pill" style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623" }}>
                            🚴 3 métiers logistique / livraison
                        </span>
                        <span className="pill" style={{ background: "rgba(245,166,35,0.15)", color: "#F5A623" }}>
                            🧹 1 métier entretien
                        </span>
                    </div>
                </div>
            </section>

            {/* GRID BY FAMILY */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
                {Object.entries(families).map(([family, list]) => (
                    <div key={family} className="mb-14" data-testid={`family-${family.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="overline mb-3 text-[#F5A623]">Famille</div>
                        <h2 className="font-display text-2xl md:text-3xl text-[#0B192C] mb-6">
                            {family}
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {list.map((j) => (
                                <Link
                                    key={j.slug}
                                    to={`/metiers/${j.slug}`}
                                    className="group sd-card p-6 hover:border-[#F5A623] transition-all"
                                    data-testid={`metier-card-${j.slug}`}
                                >
                                    <div className="text-3xl mb-3">{j.emoji}</div>
                                    <h3 className="font-display text-lg text-[#0B192C] mb-2 group-hover:text-[#F5A623] transition-colors">
                                        {j.slug
                                            .replace(/-/g, " ")
                                            .replace(/^\w/, (c) => c.toUpperCase())}
                                    </h3>
                                    <p className="text-xs text-slate-500 line-clamp-3 mb-4">
                                        {j.client_pitch}
                                    </p>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                                            <Wallet size={11} />
                                            {j.salary_range}
                                        </span>
                                        <span className="text-[#F5A623] font-semibold inline-flex items-center gap-1">
                                            Découvrir <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            {/* CTA BAND */}
            <section className="bg-slate-50 border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
                    <div className="overline mb-3 text-[#F5A623]">Prêt à démarrer ?</div>
                    <h2 className="font-display text-3xl md:text-4xl text-[#0B192C] mb-4">
                        Un besoin en 60 secondes, une candidature en 24 h
                    </h2>
                    <p className="text-slate-500 max-w-2xl mx-auto mb-8">
                        Publiez votre besoin ou inscrivez-vous comme professionnel. Aucun
                        abonnement, aucun engagement — la mise en relation est gratuite,
                        les frais plateforme (15 % côté entreprise + 8 % côté pro) ne sont
                        prélevés qu'en cas de mission validée.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/devis" className="btn-gold" data-testid="metiers-hub-cta-devis">
                            Je cherche du personnel
                            <ArrowRight className="inline ml-1 -mt-1" size={16} />
                        </Link>
                        <Link
                            to="/professionnels"
                            className="btn-ghost-navy"
                            data-testid="metiers-hub-cta-pro"
                        >
                            <MapPin size={14} className="inline mr-1 -mt-0.5" />
                            Je cherche des missions
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
